import React from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty, chunk } from 'lodash';
import withRouter from 'react-router/withRouter';

import loadable from '@loadable/component';

const NotFound = loadable(() =>
  import('../../../../components/not_found_component/not_found_component')
);
const LoadingSpinner = loadable(() =>
  import('../../../../components/loading_spinner/loading_spinner')
);
const BwfiLayout = loadable(
  () => import('../../../../fc-components/main/containers'),
  {
    resolveComponent: components => components.BwfiLayout
  }
);
const Button = loadable(
  () => import('../../../../fc-components/main/components'),
  {
    resolveComponent: components => components.Button
  }
);
const IconTitle = loadable(
  () => import('../../../../fc-components/main/components'),
  {
    resolveComponent: components => components.IconTitle
  }
);
const Image = loadable(
  () => import('../../../../fc-components/main/components'),
  {
    resolveComponent: components => components.Image
  }
);
const Section = loadable(
  () => import('../../../../fc-components/main/components'),
  {
    resolveComponent: components => components.Section
  }
);
const Text = loadable(
  () => import('../../../../fc-components/main/components'),
  {
    resolveComponent: components => components.Text
  }
);

const BwiApplyPage = props => {
  const fullPackageObj = get(props, 'franchisePage', null);
  const packageData = get(fullPackageObj, 'data', {});

  const packageBlurb = get(packageData, 'acf.franchise_blurb', '');
  const categoryBlurb = get(
    packageData,
    'acf.application_page.categories[0].categoryGroup.description',
    ''
  );
  const acceptingApplications = get(
    packageData,
    'acf.application_page.acceptingApplications',
    undefined
  );
  const packageDeadline = get(
    packageData,
    'acf.application_page.deadline',
    undefined
  );
  const packageApplyUrl = get(
    packageData,
    'acf.application_page.applyUrl',
    '#'
  );
  const hideJudges = get(
    packageData,
    'acf.application_page.hide_judges',
    false
  );
  const pastJudgesBlurb = get(
    packageData,
    'acf.application_page.pastJudges',
    ''
  );
  const packageButtonText = get(
    packageData,
    'acf.application_page.buttonText',
    '#'
  );
  const packageCategories = get(
    packageData,
    'acf.application_page.categories',
    []
  );
  const packageWhyApply = get(packageData, 'acf.application_page.whyApply', []);
  const whyApplyColumns = chunk(
    packageWhyApply,
    (packageWhyApply.length + 1) / 2
  );
  const franchiseFeatureImageDesktop = get(
    packageData,
    'acf.franchise_images.franchise_animated_svg_desktop'
  );

  // Initiate from Redux
  const { franchisePage, status } = props;

  // Handle Lazy Loading & Errors
  if (franchisePage.error || status === 404 || isEmpty(fullPackageObj)) {
    return (
      <section className="best-workplaces-for-innovators-page">
        <article className="best-workplaces-for-innovators__main--error-pg">
          <NotFound />
        </article>
      </section>
    );
  }

  if (franchisePage.isLoading) {
    return (
      <section className="best-workplaces-for-innovators-page">
        <article className="best-workplaces-for-innovators__main">
          <LoadingSpinner />
        </article>
      </section>
    );
  }

  return (
    <BwfiLayout>
      <Section name="topHero" maxWidth={688} bgColor="#FEED01">
        <div className="topItems">
          <Image image={franchiseFeatureImageDesktop} />
          <Text as="h2" color="#000000">
            {packageDeadline}
          </Text>
          {acceptingApplications && (
            <a href={packageApplyUrl}>
              <Button
                rounded
                bgColor="#000000"
                labelColor="#ffffff"
                hoverColor="#FFFFFF"
                label={packageButtonText}
              />
            </a>
          )}
        </div>
      </Section>

      <Section name="blurb" maxWidth={860} vMargins="20">
        <Text as="div">
          <div dangerouslySetInnerHTML={{ __html: packageBlurb }} />
        </Text>
        {acceptingApplications && (
          <a href={packageApplyUrl}>
            <Button
              bgColor="#FEED01"
              labelColor="#000000"
              labelHoverColor="#000000"
              hoverColor="#D7D7D7"
              rounded
              label={packageButtonText}
            />
          </a>
        )}
        <div className="deadlineSubText">
          <Text as="h6" color="#000000">
            {packageDeadline}
          </Text>
        </div>
      </Section>

      <Section name="whyApply" maxWidth={860} bgColor="#000000">
        <div className="sectionHeading">
          <IconTitle
            title="WHY APPLY"
            color="#ffffff"
            underline
            underlineColor="#ffffff"
          />
        </div>
        <div className="reasonContainer">
          {whyApplyColumns.map((column, i) => (
            <div key={`whyApply-column-${i}`} className="whyApply-column">
              {column.map((reason, index) => (
                <div key={`whyApply-${index}`} className="whyApplyItem">
                  <div className="icon">
                    <Image image={get(reason, 'icon.url')} />
                  </div>
                  <div>
                    <Text as="h3" color="#ffffff">
                      {reason.title}
                    </Text>
                    <Text
                      fontFamily="'CentraNo2Book', sans-serif"
                      fontSize="15px"
                      lineHeight="17px"
                      as="div"
                      color="#cccccc"
                    >
                      {reason.description}
                    </Text>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </Section>

      <Section name="categories" maxWidth={860}>
        <div className="sectionHeading">
          <IconTitle title="CATEGORIES" underline />
        </div>
        <Text as="div">
          <div dangerouslySetInnerHTML={{ __html: categoryBlurb }} />
        </Text>
        <div>
          {packageCategories.map((category, index) => {
            const categoryGroup = get(category, 'categoryGroup', {});
            const categoryName = get(categoryGroup, 'name', ''); // eslint-disable-line
            const categoryItems = get(categoryGroup, 'items', []);

            return (
              <div
                key={`category-container-${index}`}
                className="categoryContainer"
              >
                <div className="columnOne">
                  {categoryItems.map((item, i) => {
                    if (i % 2 === 0) {
                      return (
                        <div key={`category-item-${i}`} className="categoyCell">
                          <Text as="h3">
                            {item.title}{' '}
                            {item.subHed ? <span>{item.subHed}</span> : ''}
                          </Text>
                          <Text
                            fontFamily="'CentraNo2Book', sans-serif"
                            fontSize="15px"
                            lineHeight="17px"
                            as="div"
                            color="#5f5f5f"
                          >
                            {item.description}
                          </Text>
                        </div>
                      );
                    }
                    return '';
                  })}
                </div>
                <div className="columnTwo">
                  {categoryItems.map((item, i) => {
                    if (i % 2 === 1) {
                      return (
                        <div key={`category-item-${i}`} className="categoyCell">
                          <Text as="h3">
                            {item.title}{' '}
                            {item.subHed ? <span>{item.subHed}</span> : ''}
                          </Text>
                          <Text
                            fontFamily="'CentraNo2Book', sans-serif"
                            fontSize="15px"
                            lineHeight="17px"
                            as="div"
                            color="#5f5f5f"
                          >
                            {item.description}
                          </Text>
                        </div>
                      );
                    }
                    return '';
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {!hideJudges && (
        <Section maxWidth={860} vMargins="20">
          <div className="sectionHeading">
            <IconTitle title="PAST JUDGES" underline />
          </div>
          <Text as="div">
            <div dangerouslySetInnerHTML={{ __html: pastJudgesBlurb }} />
          </Text>
        </Section>
      )}

      <Section name="finalApply" maxWidth={860}>
        {acceptingApplications && (
          <a href={packageApplyUrl}>
            <Button
              bgColor="#FEED01"
              labelHoverColor="#000000"
              labelColor="#000000"
              hoverColor="#D7D7D7"
              rounded
              label={packageButtonText}
            />
          </a>
        )}
        <div className="deadlineSubText">
          <Text as="h6" color="#000000">
            {packageDeadline}
          </Text>
        </div>
      </Section>
    </BwfiLayout>
  );
};

BwiApplyPage.propTypes = {
  status: PropTypes.number.isRequired,
  franchisePage: PropTypes.shape({
    isLoading: PropTypes.bool,
    error: PropTypes.bool,
    errorMessage: PropTypes.string
  }).isRequired
};

export default withRouter(BwiApplyPage);
